import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { GridEditor } from '@/components/schedule/grid-editor';
import { RulesEditor } from '@/components/schedule/rules-editor';
import { BlockList } from '@/components/schedule/block-list';
import {
  ServiceSchedulingDraft,
  ServiceSchedulingEditor,
} from '@/components/schedule/service-scheduling-editor';
import {
  useAvailabilityGrid,
  useScheduleSettings,
  useSetAvailabilityGrid,
  useUpdateScheduleSettings,
} from '@/hooks/use-appointments';
import { useProviderServices, useSaveServiceScheduling } from '@/hooks/use-services';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { AvailabilityInterval, ScheduleSettings } from '@/types/appointment';
import { timeToMinutes } from '@/utils/format-appointment';
import { useThemedHex, useSolidChip } from '@/hooks/use-theme-colors';

type Tab = 'grade' | 'servicos' | 'regras' | 'bloqueios';

const TABS: {
  key: Tab;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
}[] = [
  {
    key: 'grade',
    label: 'Horários',
    icon: 'time-outline',
    title: 'Quando você atende',
    description:
      'Ligue os dias da semana e defina as faixas de horário. É daqui que saem os horários livres que o cliente vê.',
  },
  {
    key: 'servicos',
    label: 'Serviços',
    icon: 'briefcase-outline',
    title: 'O que pode ser marcado',
    description:
      'Só serviço com duração vira horário na agenda. Defina a duração e quantas vagas cada horário tem.',
  },
  {
    key: 'regras',
    label: 'Regras',
    icon: 'options-outline',
    title: 'Como o cliente marca',
    description:
      'Antecedência para marcar e cancelar, limite por cliente e lembrete no WhatsApp. Os padrões já funcionam.',
  },
  {
    key: 'bloqueios',
    label: 'Bloqueios',
    icon: 'lock-closed-outline',
    title: 'Folgas e imprevistos',
    description:
      'Datas em que você não atende, mesmo tendo horário na grade. Férias, feriado, compromisso pessoal.',
  },
];

const SAVE_LABEL: Record<Tab, string> = {
  grade: 'Salvar horários',
  servicos: 'Salvar serviços',
  regras: 'Salvar regras',
  bloqueios: 'Salvar',
};

/** Chave estável da grade, para saber se o rascunho difere do que está salvo. */
function gridKey(intervals: AvailabilityInterval[]): string {
  return intervals
    .map((i) => `${i.weekday}-${i.startTime}-${i.endTime}`)
    .sort()
    .join('|');
}

function formatHours(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}min`;
  return rest ? `${hours}h${String(rest).padStart(2, '0')}` : `${hours}h`;
}

function ChecklistRow({
  done,
  label,
  hint,
  onPress,
}: {
  done: boolean;
  label: string;
  hint: string;
  onPress: () => void;
}) {
  const solid = useSolidChip();
  const th = useThemedHex();
  return (
    <PressableScale
      haptic
      onPress={onPress}
      className="flex-row items-center gap-3 py-2"
      accessibilityLabel={`${label}: ${done ? 'pronto' : 'pendente'}`}
    >
      <View
        className="h-6 w-6 items-center justify-center rounded-full"
        style={{
          backgroundColor: solid.bg(
            done ? th('#ECFDF5') : th('#FFF7ED'),
            done ? '#16A34A' : '#FD5001',
          ),
        }}
      >
        <Ionicons
          name={done ? 'checkmark' : 'ellipse-outline'}
          size={14}
          color={solid.icon(done ? th('#047857') : '#FD5001')}
        />
      </View>
      <View className="min-w-0 flex-1">
        <ThemedText className="font-semibold text-sm text-neutral-900">{label}</ThemedText>
        <ThemedText className="text-xs text-neutral-500">{hint}</ThemedText>
      </View>
      <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
    </PressableScale>
  );
}

export default function ScheduleSettingsScreen() {
  const solid = useSolidChip();
  const th = useThemedHex();
  const { addToast } = useToastStore();
  const settings = useScheduleSettings();
  const grid = useAvailabilityGrid();
  const setGrid = useSetAvailabilityGrid();
  const updateSettings = useUpdateScheduleSettings();
  const saveServices = useSaveServiceScheduling();
  const services = useProviderServices({ limit: 100 });

  const [tab, setTab] = useState<Tab>('grade');
  const [gridDraft, setGridDraft] = useState<AvailabilityInterval[]>([]);
  const [servicesDraft, setServicesDraft] = useState<ServiceSchedulingDraft>({});
  const [rules, setRules] = useState<ScheduleSettings | null>(null);
  const [gridSeeded, setGridSeeded] = useState(false);
  const [rulesSeeded, setRulesSeeded] = useState(false);

  useEffect(() => {
    if (!gridSeeded && grid.data) {
      setGridDraft(grid.data);
      setGridSeeded(true);
    }
  }, [gridSeeded, grid.data]);

  useEffect(() => {
    if (!rulesSeeded && settings.data) {
      setRules(settings.data);
      setRulesSeeded(true);
    }
  }, [rulesSeeded, settings.data]);

  const patchRules = (patch: Partial<ScheduleSettings>) =>
    setRules((prev) => (prev ? { ...prev, ...patch } : prev));

  const patchService = useCallback<
    React.ComponentProps<typeof ServiceSchedulingEditor>['onChange']
  >((id, patch) => setServicesDraft((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } })), []);

  const saveServicesDraft = () => {
    // Um PATCH por serviço alterado, não um por toque.
    const patches = Object.entries(servicesDraft).map(([id, patch]) => ({ id, ...patch }));
    if (patches.length === 0) return;

    saveServices.mutate(patches, {
      onSuccess: () => {
        setServicesDraft({});
        addToast('Serviços salvos.', 'success');
      },
      onError: (e: any) => addToast(getApiErrorMessage(e, 'Falha ao salvar.'), 'error'),
    });
  };

  const saveGrid = () => {
    setGrid.mutate(gridDraft, {
      onSuccess: () => addToast('Horários salvos. Seus clientes já podem marcar.', 'success'),
      onError: (e: any) => addToast(getApiErrorMessage(e, 'Falha ao salvar.'), 'error'),
    });
  };

  const saveRules = () => {
    if (!rules) return;
    updateSettings.mutate(
      {
        minBookingLeadMinutes: rules.minBookingLeadMinutes,
        minCancelLeadMinutes: rules.minCancelLeadMinutes,
        bookingHorizonDays: rules.bookingHorizonDays,
        maxFuturePerClient: rules.maxFuturePerClient,
        reminderEnabled: rules.reminderEnabled,
        reminderLeadMinutes: rules.reminderLeadMinutes,
      },
      {
        onSuccess: () => addToast('Regras salvas.', 'success'),
        onError: (e: any) => addToast(getApiErrorMessage(e, 'Falha ao salvar.'), 'error'),
      },
    );
  };

  // O rascunho vence o servidor: o resumo reflete o que está na tela agora.
  const schedulableCount = useMemo(() => {
    const list = services.data?.data ?? [];
    return list.filter((service) => {
      const draft = servicesDraft[service.id];
      const duration =
        draft && 'durationMinutes' in draft ? draft.durationMinutes : service.durationMinutes;
      return !!duration;
    }).length;
  }, [services.data?.data, servicesDraft]);

  const activeDaysCount = useMemo(() => new Set(gridDraft.map((i) => i.weekday)).size, [gridDraft]);
  const weeklyMinutes = useMemo(
    () =>
      gridDraft.reduce(
        (total, i) => total + Math.max(0, timeToMinutes(i.endTime) - timeToMinutes(i.startTime)),
        0,
      ),
    [gridDraft],
  );

  const hasGrid = activeDaysCount > 0;
  const hasSchedulableService = schedulableCount > 0;
  const isLive = hasGrid && hasSchedulableService;

  const gridDirty = gridSeeded && gridKey(gridDraft) !== gridKey(grid.data ?? []);
  const rulesDirty =
    !!rules && !!settings.data && JSON.stringify(rules) !== JSON.stringify(settings.data);
  const servicesDirty = Object.keys(servicesDraft).length > 0;

  const dirtyByTab: Record<Tab, boolean> = {
    grade: gridDirty,
    servicos: servicesDirty,
    regras: rulesDirty,
    bloqueios: false,
  };

  if (settings.isLoading || !rules) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas">
        <ActivityIndicator size="large" color="#FD5001" />
      </View>
    );
  }

  const savePending = setGrid.isPending || updateSettings.isPending || saveServices.isPending;
  const showFooter = tab !== 'bloqueios' && dirtyByTab[tab];
  const activeTab = TABS.find((item) => item.key === tab)!;

  const onSave = () => {
    if (tab === 'grade') return saveGrid();
    if (tab === 'regras') return saveRules();
    return saveServicesDraft();
  };

  return (
    <PageLayout
      title="Configurar agenda"
      onBack={() => router.back()}
      footer={
        showFooter ? (
          <View className="border-t border-neutral-200 bg-card p-4">
            <PressableScale
              haptic
              disabled={savePending}
              onPress={onSave}
              className="w-full flex-row items-center justify-center gap-2 rounded-full bg-primary py-4"
              style={{ borderCurve: 'continuous', opacity: savePending ? 0.5 : 1 }}
            >
              {savePending ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <>
                  <Ionicons name="save-outline" size={19} color="#fff" />
                  <ThemedText className="font-bold text-base text-white">
                    {SAVE_LABEL[tab]}
                  </ThemedText>
                </>
              )}
            </PressableScale>
          </View>
        ) : undefined
      }
    >
      {/* Estado da agenda: o que já está de pé e o que falta para receber marcações */}
      <View
        className="gap-1 rounded-2xl border bg-card p-4"
        style={{
          borderCurve: 'continuous',
          borderColor: isLive ? '#04785733' : '#FD500133',
        }}
      >
        <View className="flex-row items-center gap-3">
          <View
            className="h-10 w-10 items-center justify-center rounded-full"
            style={{
              backgroundColor: solid.bg(
                isLive ? th('#ECFDF5') : th('#FFF7ED'),
                isLive ? '#16A34A' : '#FD5001',
              ),
            }}
          >
            <Ionicons
              name={isLive ? 'checkmark-circle' : 'alert-circle-outline'}
              size={22}
              color={solid.icon(isLive ? th('#047857') : '#FD5001')}
            />
          </View>
          <View className="min-w-0 flex-1">
            <ThemedText className="font-bold text-base text-neutral-900">
              {isLive ? 'Agenda no ar' : 'Agenda ainda não recebe marcações'}
            </ThemedText>
            <ThemedText className="text-xs leading-4 text-neutral-500">
              {isLive
                ? `${activeDaysCount} ${activeDaysCount === 1 ? 'dia' : 'dias'} · ${formatHours(weeklyMinutes)} por semana · ${schedulableCount} ${schedulableCount === 1 ? 'serviço' : 'serviços'} agendáveis`
                : 'Faltam os itens marcados abaixo. Toque em cada um para resolver.'}
            </ThemedText>
          </View>
        </View>

        <View className="mt-2 border-t border-neutral-100 pt-1">
          <ChecklistRow
            done={hasGrid}
            label="Dias e horários"
            hint={
              hasGrid
                ? `${activeDaysCount} ${activeDaysCount === 1 ? 'dia ligado' : 'dias ligados'} · ${formatHours(weeklyMinutes)} por semana`
                : 'Nenhum dia ligado ainda'
            }
            onPress={() => setTab('grade')}
          />
          <ChecklistRow
            done={hasSchedulableService}
            label="Serviços agendáveis"
            hint={
              hasSchedulableService
                ? `${schedulableCount} com duração definida`
                : 'Nenhum serviço com duração'
            }
            onPress={() => setTab('servicos')}
          />
          <ChecklistRow
            done
            label="Regras e bloqueios"
            hint="Opcional — antecedência, lembrete e folgas"
            onPress={() => setTab('regras')}
          />
        </View>
      </View>

      {/* Abas */}
      <ScrollView
        keyboardShouldPersistTaps="handled"
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ flexGrow: 0, flexShrink: 0 }}
        contentContainerStyle={{ gap: 8, paddingVertical: 2, alignItems: 'center' }}
      >
        {TABS.map(({ key, label, icon }) => {
          const active = tab === key;
          return (
            <PressableScale
              key={key}
              haptic
              onPress={() => setTab(key)}
              className="flex-row items-center gap-1.5 rounded-full px-4 py-2"
              style={{
                backgroundColor: active ? '#FD5001' : th('#fff'),
                borderWidth: 1,
                borderColor: active ? '#FD5001' : th('#E5E5E5'),
                borderCurve: 'continuous',
              }}
            >
              <Ionicons name={icon} size={14} color={active ? '#fff' : th('#737373')} />
              <ThemedText
                className="font-semibold text-sm"
                style={{ color: active ? '#fff' : th('#525252') }}
              >
                {label}
              </ThemedText>
              {dirtyByTab[key] && (
                <View
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: active ? th('#fff') : '#FD5001' }}
                />
              )}
            </PressableScale>
          );
        })}
      </ScrollView>

      {/* Cabeçalho da aba: o que esta seção resolve */}
      <View className="gap-0.5 px-0.5">
        <ThemedText className="font-bold text-base text-neutral-900">{activeTab.title}</ThemedText>
        <ThemedText className="text-xs leading-4 text-neutral-500">
          {activeTab.description}
        </ThemedText>
      </View>

      {tab === 'grade' && <GridEditor value={gridDraft} onChange={setGridDraft} />}
      {tab === 'servicos' && (
        <ServiceSchedulingEditor value={servicesDraft} onChange={patchService} />
      )}
      {tab === 'regras' && <RulesEditor value={rules} onChange={patchRules} />}
      {tab === 'bloqueios' && <BlockList />}
    </PageLayout>
  );
}
